import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../utils/FormateDateTime'
import { CalculateAge, calculateDetailedAge } from '../../utils/CalculateAge'
import { pdfjs } from 'react-pdf'
import moment from 'moment'
import ChaiReportHeader from '../../shared/ChaiReportHeader'
import ChaiReportFooter from '../../shared/ChaiReportFooter'
import useMedicalReportSettings from '../../hooks/useMedicalReportSettings'
import ReportBarcode from './ReportBarcode'
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const LabReportPrint = ({ reportData, patient, practitioner }) => {
  const { t, i18n } = useTranslation()
  const { isChaiFormat, businessInfo: buisnessInfo } = useMedicalReportSettings()
  const [pdfImages, setPdfImages] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);

  const reportSettings = reportData?.data?.settings || reportData?.settings ||
    (Array.isArray(reportData) ? reportData[0]?.settings : undefined) || [];
  const getReportSetting = (key, fallback) => {
    const setting = Array.isArray(reportSettings)
      ? reportSettings.find(item => item?.key === key)
      : reportSettings?.[key];
    return setting?.value ?? setting ?? fallback;
  };
  const isReportSettingEnabled = (key, fallback) => {
    const value = getReportSetting(key, fallback);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return !['false', '0', 'no', 'off'].includes(value.toLowerCase());
    return Boolean(value);
  };
  const showCbahiLogo = isReportSettingEnabled('is_cbahi_logo', isChaiFormat);
  const showStandardLogo = isReportSettingEnabled('is_standard_logo', true);
  const showBarcode = isReportSettingEnabled('show_barcode', isChaiFormat);
  const reportPrintHeader = String(getReportSetting('report_print_header', '')).toLowerCase();
  const printReportLayout = getReportSetting('print_report_layout', 'compact_report');
  const isComprehensiveReport = printReportLayout === 'comprehensive_report';
  const useCbahiHeader = reportPrintHeader
    ? reportPrintHeader.includes('cbahi')
    : showCbahiLogo || isChaiFormat;

  const getAggregateDates = (reportsArray) => {
    const dates = {
      collected: '---',
      received: '---',
      uploaded: '---',
      receivedBy: '---',
      collectedBy: '---',
      uploadedBy: '---',
      approvedBy: '---',
      signatureUrl: '',
      stampUrl: ''
    };

    let timestamps = {
      collected: 0,
      received: 0,
      uploaded: 0
    };

    if (!Array.isArray(reportsArray)) return dates;

    reportsArray.forEach(report => {
      const history = report.history || report.data?.history;
      if (!Array.isArray(history)) return;

      history.forEach(h => {
        const reason = (h.action_reason || '').toLowerCase();
        const time = h.action_performers?.time || h.action_info?.at;
        const performer = h.action_performers?.name || h.action_info?.by_name;
        const currentTs = time ? new Date(time).getTime() : 0;

        if (!currentTs) return;

        if (reason?.includes('sample collected')) {
          timestamps.collected = currentTs;
          dates.collected = moment.parseZone(time).format('DD MMM YYYY hh:mm A');
          dates.collectedBy = performer || '---';
        } else if (reason?.includes('order item received') || reason?.includes('order received')) {
          timestamps.received = currentTs;
          dates.received = moment.parseZone(time).format('DD MMM YYYY hh:mm A');
          dates.receivedBy = performer || '---';
        } else if (reason?.includes('result uploaded') || reason?.includes('result upload') || reason?.includes('order item result uploaded')) {
          timestamps.uploaded = currentTs;
          dates.uploaded = moment.parseZone(time).format('DD MMM YYYY hh:mm A');
          dates.uploadedBy = performer || '---';
        } else if (reason?.includes('order item approved') || reason?.includes('approved')) {
          dates.approvedBy = performer || '---';
          dates.signatureUrl = h.action_info?.signature_url || h.action_performers?.signature_url || dates.signatureUrl;
          dates.stampUrl = h.action_info?.stamp_url || h.action_performers?.stamp_url || dates.stampUrl;
        }
      });
    });

    if (dates.collected === '---') {
      const firstReport = reportsArray[0];
      const fallbackDate = firstReport?.authored_on || firstReport?.data?.authored_on || firstReport?.created_at || firstReport?.data?.created_at;
      if (fallbackDate) {
        dates.collected = moment.parseZone(fallbackDate).format('DD MMM YYYY hh:mm A');
      }
    }

    return dates;
  };

  const unwrapReportData = (value) => {
    let data = value;
    if (data?.data?.result) data = data.data.result;
    else if (data?.result) data = data.result;
    else if (data?.data && !Array.isArray(data.data)) data = data.data;
    return data;
  };

  const createHistory = (item) => {
    if (Array.isArray(item?.history)) return item.history;

    const history = [];
    const addHistory = (info, reason) => {
      if (!info?.at) return;
      history.push({
        action_info: info,
        action_reason: reason,
        action_type: item?.item_type || 'EXTERNAL'
      });
    };

    addHistory(item?.received_info, 'Order Item Received');
    addHistory(item?.result_upload_info, 'Order Item Result Uploaded');
    addHistory(item?.approved_info, 'Order Item Approved');
    return history;
  };

  const normalizeItem = (item = {}) => {
    const upload = item.result_upload || item;
    const attachmentUrl = upload?.file_url || upload?.attachments?.find(a => a?.url)?.url;

    return {
      ...upload,
      file: attachmentUrl || (typeof upload?.file === 'string' && /^https?:\/\//i.test(upload.file) ? upload.file : ''),
      file_key: upload?.file,
      barcode_data: item.barcode_data || upload?.barcode_data,
      history: createHistory(item),
      lab_test_name:
        item.item_name ||
        item.non_standard_desc ||
        item.service_desc ||
        upload?.lab_test?.lab_test_name ||
        upload?.lab_test_name,
      test_category:
        item.test_category ||
        item.lab_test_category ||
        item.category ||
        upload?.test_category ||
        upload?.lab_test_category ||
        upload?.category,
      item_id: item.item_id || item.id || upload?.item_id
    };
  };

  const reportPayload = unwrapReportData(reportData);
  const rawReports = Array.isArray(reportPayload) ? reportPayload : (reportPayload ? [reportPayload] : []);

  let processedItems = [];
  let patientInfoSource = null;
  let practitionerInfoSource = null;
  let orderNoSource = '';

  if (rawReports.length > 0) {
    const firstRep = rawReports[0];
    const responseItems = Array.isArray(firstRep?.items)
      ? firstRep.items
      : Array.isArray(firstRep?.order_items)
        ? firstRep.order_items
        : null;

    if (responseItems) {
      processedItems = responseItems
        .filter(item => item?.result_upload)
        .map(normalizeItem);
      patientInfoSource = firstRep.patient_info || firstRep.patient;
      practitionerInfoSource = firstRep.practitioner_info || firstRep.practitioner;
      orderNoSource = firstRep.order_number || firstRep.order_no;
    } else if (firstRep.result_upload) {
      processedItems = [normalizeItem(firstRep)];
      orderNoSource = firstRep.order_number || firstRep.order_no || firstRep.result_upload?.order_no;
      patientInfoSource = firstRep.patient_info || firstRep.patient || firstRep.result_upload?.patient;
      practitionerInfoSource = firstRep.practitioner_info || firstRep.practitioner || firstRep.result_upload?.practitioner;
    } else {
      processedItems = rawReports;
    }
  }

  const reports = processedItems;

  const sortedReports = React.useMemo(() => {
    return [...reports].sort((a, b) => {
      if (isComprehensiveReport) {
        const aCategory = String(a?.test_category || a?.category || a?.lab_test?.category || '').toLowerCase();
        const bCategory = String(b?.test_category || b?.category || b?.lab_test?.category || '').toLowerCase();
        const categoryOrder = aCategory.localeCompare(bCategory);
        if (categoryOrder !== 0) return categoryOrder;
      }
      const aComps = a?.components || a?.data?.components || [];
      const bComps = b?.components || b?.data?.components || [];
      return aComps.length - bComps.length;
    });
  }, [reports, isComprehensiveReport]);

  const firstReport = reports[0] || {};

  const displayPatient = patient || patientInfoSource || firstReport?.patient || firstReport?.data?.patient || {
    name: firstReport?.patient_id || firstReport?.data?.patient_id || '---'
  };
  const displayPractitioner = practitioner || practitionerInfoSource || firstReport?.practitioner || firstReport?.data?.practitioner || {
    name: firstReport?.practitioner_id || firstReport?.data?.practitioner_id || '---'
  };

  const orderNo = orderNoSource || firstReport?.order_no || firstReport?.data?.order_no || '---';
  const reportBarcode = firstReport?.barcode_data || firstReport?.result_upload?.barcode_data || orderNo;
  const showReportBarcode = showBarcode && Boolean(reportBarcode && reportBarcode !== '---');
  const orderDates = getAggregateDates(reports);
  const businessLogo = (() => {
    if (!buisnessInfo) return '';
    let logo = buisnessInfo?.logo_url || buisnessInfo?.logo || '';
    if (logo && !logo.startsWith('data:') && !logo.startsWith('http')) {
      logo = `data:image/jpeg;base64,${logo}`;
    }
    return logo;
  })();
  const reportLabel = (key, fallback = key) => {
    const translated = t(key, { nsSeparator: false });
    return typeof translated === 'string' && translated.trim() ? translated : fallback;
  };

  const repeatedPrintHeader = (
    <div className='print-report-header bg-white'>
      {useCbahiHeader ? (
        <ChaiReportHeader
          reportName={t('Laboratory Report')}
          businessInfo={buisnessInfo}
          showChaiLogo={showCbahiLogo}
          showBusinessLogo={showStandardLogo}
          settingsOverride
        />
      ) : (
        <div className='flex justify-between items-end mb-2 px-3 pt-3 pb-2'>
          <div className='w-[30%] text-[11px] text-gray-600'>
            <div><strong>License No:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo || 'N/A'}</div>
            <div><strong>CR No:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}</div>
            <div><strong>Phone:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}</div>
            <div><strong>Email:</strong> {buisnessInfo?.email || 'N/A'}</div>
          </div>
          <div className='w-[40%] flex flex-col items-center'>
            {showStandardLogo && businessLogo && <img className='h-[65px] mb-2 object-contain' src={businessLogo} alt='Business Logo' />}
            <p className='text-red-700 font-bold text-[14px] text-center mb-1 leading-tight'>{buisnessInfo?.name_en || 'Business Name'}<br />{buisnessInfo?.name_ar || 'Business Name'}</p>
            <p className='text-gray-600 text-[12px] font-semibold text-center border-t border-gray-100 pt-1'>{t('Laboratory Report')}</p>
          </div>
          <div className='w-[30%] text-[11px] text-gray-600 text-right' dir='rtl'>
            <div><strong>رقم ترخيص:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo || 'N/A'}</div>
            <div><strong>رقم السجل التجاري:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}</div>
            <div><strong>الهاتف:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}</div>
            <div><strong>البريد الإلكتروني:</strong> {buisnessInfo?.email || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className='mx-3 mb-2 border border-black p-2 text-[12px] rounded-sm bg-white'>
        <div className='grid grid-cols-2 gap-x-8 gap-y-[1px]'>
          <div className='col-span-2 flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Patient Name')}:</strong><span className='capitalize font-semibold text-gray-900'>{displayPatient?.name || displayPatient?.full_name || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Type')}:</strong><span className='text-gray-900 font-semibold'>{reportLabel('Laboratory')}</span></div>
          <div className='flex items-start gap-2'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Order Number')}:</strong><div className='flex min-w-0 flex-col items-start gap-1'><span className='whitespace-nowrap text-gray-900 font-semibold'>{orderNo}</span>{showReportBarcode && <ReportBarcode value={reportBarcode} />}</div></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('MRN')}:</strong><span className='text-gray-900 font-semibold'>{displayPatient?.file_no || displayPatient?.file_number || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Doctor')}:</strong><span className='text-gray-900 font-semibold'>{displayPractitioner?.name || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Document ID')}:</strong><span className='text-gray-900 font-semibold'>{displayPatient?.document_id || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Collection Date')}:</strong><span className='text-gray-900 font-semibold'>{orderDates.collected}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Phone')}:</strong><span className='text-gray-900 font-semibold'>{displayPatient?.phone || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Receiving Date')}:</strong><span className='text-gray-900 font-semibold'>{orderDates.received}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Age / Gender')}:</strong><span className='text-gray-900 font-semibold'>{CalculateAge(displayPatient?.dob || displayPatient?.birth_date) || '---'} / {displayPatient?.gender || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Result Date')}:</strong><span className='text-gray-900 font-semibold'>{orderDates.uploaded}</span></div>
        </div>
      </div>
    </div>
  );

  const getReportId = (report) => report?._id || report?.id || report?.lab_test_id || 'default';

  const ageToDays = (ageObj) => {
    if (!ageObj) return 0;
    const years = parseInt(ageObj.year ?? ageObj.years ?? ageObj.age_year_min ?? ageObj.age_year_max ?? 0);
    const months = parseInt(ageObj.month ?? ageObj.months ?? ageObj.age_month_min ?? ageObj.age_month_max ?? 0);
    const days = parseInt(ageObj.day ?? ageObj.days ?? ageObj.age_day_min ?? ageObj.age_day_max ?? 0);
    return (years * 365) + (months * 30) + days;
  };

  const isGenderMatch = (compGender, patientGender) => {
    if (!compGender || !patientGender) return true;
    const pGender = String(patientGender).toLowerCase();
    if (['both', 'all', 'n/a', ''].includes(pGender)) return true;

    try {
      let genders = [];
      if (Array.isArray(compGender)) {
        genders = compGender;
      } else {
        try {
          const parsed = JSON.parse(compGender);
          genders = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          genders = [compGender];
        }
      }

      const normalizedGenders = genders.map(g => (typeof g === 'object' ? g?.value || g?.label : g)?.toLowerCase());
      return normalizedGenders.includes("both") || normalizedGenders.includes("all") || normalizedGenders.includes(pGender);
    } catch {
      const cGender = String(compGender).toLowerCase();
      return cGender.includes("both") || cGender.includes("all") || cGender.includes(pGender);
    }
  };

  useEffect(() => {
    const convertAllPdfs = async () => {
      try {
        setPdfLoading(true);
        const allImages = {};

        for (const report of reports) {
          if (report?.result_type === "EXTERNAL" && report?.file) {
            const reportId = getReportId(report);
            const images = await convertPdfToImages(report.file);
            allImages[reportId] = images;
          }
        }

        setPdfImages(allImages);
      } catch (error) {
        console.error("Error converting PDFs to images:", error);
      } finally {
        setPdfLoading(false);
      }
    };

    const convertPdfToImages = async (pdfUrl) => {
      const pdf = await pdfjs.getDocument(pdfUrl).promise;
      const images = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: ctx, viewport }).promise;
        images.push(canvas.toDataURL("image/png"));
      }
      return images;
    };

    if (reports.some(r => r?.result_type === "EXTERNAL" && r?.file)) {
      convertAllPdfs();
    }
  }, [reportData]);

  const getRangeValues = (min, max) => {
    if (min === undefined && max === undefined) return 'N/A - N/A'
    return `${min ?? '-'} - ${max ?? '-'}`
  }

  const getResultStatus = (value, minRange, maxRange, item) => {
    let min = minRange
    let max = maxRange

    if ((min === undefined || min === null) && (max === undefined || max === null)) {
      if (item?.range_min !== undefined && item?.range_max !== undefined) {
        min = item.range_min
        max = item.range_max
      }
    }
    if (min === undefined || max === undefined) {
      return { status: '--', color: 'black' }
    }
    const numericValue = parseFloat(value)
    const numMin = parseFloat(min)
    const numMax = parseFloat(max)
    if (isNaN(numericValue) || isNaN(numMin) || isNaN(numMax)) {
      return { status: '--', color: 'black' }
    }
    return {
      status: numericValue < numMin ? 'Low' : numericValue > numMax ? 'High' : 'Normal',
      color: numericValue < numMin || numericValue > numMax ? 'red' : 'green'
    }
  }

  if (reports.length === 0 || !reports[0]) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">{t('No report data available')}</p>
      </div>
    )
  }

  // Common Header and Patient Info
  return (
    <div className="lab-report-container" dir={i18n.language?.startsWith('ar') ? 'rtl' : 'ltr'}>
      <div className={`report-page ${isComprehensiveReport ? 'comprehensive-report-page' : 'compact-report-page'}`}>
        <table className="print-table">
          <thead className={isComprehensiveReport ? 'comprehensive-header-repeat' : ''}>
            <tr>
              <td>
                {isComprehensiveReport ? repeatedPrintHeader : <div className="page-header-space"></div>}
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className={`print-report-content ${isComprehensiveReport ? `comprehensive-print-content ${useCbahiHeader ? '' : 'supergit-print-content'}` : 'compact-print-content'}`}>
                  <div className='print-report-header comprehensive-body-header bg-white'>
                  {/* Business Header */}
                  {useCbahiHeader ? (
                    <ChaiReportHeader
                      reportName={t("Laboratory Report")}
                      businessInfo={buisnessInfo}
                      showChaiLogo={showCbahiLogo}
                      showBusinessLogo={showStandardLogo}
                      settingsOverride
                    />
                  ) : (
                  <div className='flex justify-between items-end mb-2 px-3 pt-3 pb-2'>
                    <div className='w-[30%] text-[11px] text-gray-600'>
                      <div className='mb-1'>
                        <strong>License No:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo|| 'N/A'}
                      </div>
                      <div className='mb-1'>
                        <strong>CR No:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}
                      </div>
                      <div className='mb-1'>
                        <strong>Phone:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}
                      </div>
                      <div className='mb-1'>
                        <strong>Email:</strong> {buisnessInfo?.email || 'N/A'}
                      </div>
                    </div>

                    <div className='w-[40%] flex flex-col items-center'>
                      {showStandardLogo && businessLogo ? (
                        <img
                          className='h-[65px] mb-2 object-contain'
                          src={businessLogo}
                          alt='Business Logo'
                        />
                      ) : null}
                      <p className='text-red-700 font-bold text-[14px] text-center mb-1 leading-tight'>
                        {buisnessInfo?.name_en || 'Business Name'}
                        <br />
                        {buisnessInfo?.name_ar || 'Business Name'}
                      </p>
                      <p className='text-gray-600 text-[12px] font-semibold text-center border-t border-gray-100 pt-1'>
                       {t("Laboratory Report")}
                      </p>
                    </div>

                    <div className='w-[30%] text-[11px] text-gray-600 text-right' dir='rtl'>
                      <div className='mb-1'>
                        <strong>رقم ترخيص:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo|| 'N/A'}
                      </div>
                      <div className='mb-1'>
                        <strong>رقم السجل التجاري:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}
                      </div>
                      <div className='mb-1'>
                        <strong>الهاتف:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}
                      </div>
                      <div className='mb-1'>
                        <strong>البريد الإلكتروني:</strong> {buisnessInfo?.email || 'N/A'}
                      </div>
                    </div>
                  </div>
                  )}

                  <div className='mx-3 mb-2 border border-black p-2 mt-3 text-[12px] rounded-sm bg-white'>
                    <div className='grid grid-cols-2 gap-x-8 gap-y-[1px]'>
                      <div className='col-span-2 flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Patient Name')}:</strong>
                        <span className='capitalize font-semibold text-gray-900 whitespace-normal break-words'>{displayPatient?.name || displayPatient?.full_name || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Type')}:</strong>
                        <span className='text-gray-900 font-semibold'>{reportLabel('Laboratory')}</span>
                      </div>
                      <div className='flex items-start'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Order Number')}:</strong>
                        <div className='flex min-w-0 flex-col items-start gap-1'>
                          <span className='whitespace-nowrap text-gray-900 font-semibold'>{orderNo}</span>
                          {showReportBarcode && <ReportBarcode value={reportBarcode} />}
                        </div>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('MRN')}:</strong>
                        <span className='text-gray-900 font-semibold'>{displayPatient?.file_no || displayPatient?.file_number || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Doctor')}:</strong>
                        <span className='capitalize text-gray-900 font-semibold whitespace-normal break-words'>{displayPractitioner?.name || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Document ID')}:</strong>
                        <span className='text-gray-900 font-semibold'>{displayPatient?.document_id || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Collection Date')}:</strong>
                        <span className='text-gray-900 font-semibold'>{orderDates.collected}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Phone')}:</strong>
                        <span className='text-gray-900 font-semibold'>{displayPatient?.phone || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Receiving Date')}:</strong>
                        <span className='text-gray-900 font-semibold'>{orderDates.received}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Age / Gender')}:</strong>
                        <span className='capitalize text-gray-900 font-semibold'>{CalculateAge(displayPatient?.dob || displayPatient?.birth_date) || '---'} / {displayPatient?.gender || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{reportLabel('Result Date')}:</strong>
                        <span className='text-gray-900 font-semibold'>{orderDates.uploaded}</span>
                      </div>
                    </div>
                  </div>

                  </div>
                  {/* Loop through each report */}
                  {sortedReports.map((report, reportIndex) => {
                    const category = report?.test_category || report?.category || report?.lab_test?.category || 'Other';
                    const previousReport = sortedReports[reportIndex - 1];
                    const previousCategory = previousReport?.test_category || previousReport?.category || previousReport?.lab_test?.category || 'Other';
                    const isNewCategory = isComprehensiveReport && reportIndex > 0 && category !== previousCategory;
                    const rawComponents = report?.components || report?.data?.components || [];
                    const patientDob = displayPatient?.dob || displayPatient?.birth_date;
                    const patientGender = (displayPatient?.gender || "").toLowerCase();
                    const patientAgeDetail = calculateDetailedAge(patientDob);
                    const patientAgeInDays = ageToDays(patientAgeDetail);

                    const filteredComponents = rawComponents
                      .filter(comp => isGenderMatch(comp?.gender, patientGender))
                      .map((comp) => {
                        const ranges = comp.range || comp.age_range || [];
                        const isNumeric = comp.value_type === 'number';

                        if (!isNumeric) {
                          return {
                            ...comp,
                            minRange: ranges[0]?.value_range?.min_value ?? ranges[0]?.range_min,
                            maxRange: ranges[0]?.value_range?.max_value ?? ranges[0]?.range_max,
                          };
                        }

                        // Find best matching range
                        const matchingRangeItems = ranges.map(range => {
                          const rangeMin = ageToDays(range.min_age_range || range);
                          const rangeMax = ageToDays(range.max_age_range || {
                            day: range.age_day_max,
                            month: range.age_month_max,
                            year: range.age_year_max,
                          });

                          const r = range.max_age_range || { day: range.age_day_max, month: range.age_month_max, year: range.age_year_max };
                          let buffer = 0;
                          if ((r.day === 0 || r.day === undefined) && (r.month > 0 || r.year > 0)) {
                            buffer = 30; // buffer 1 month
                          }

                          const matchesExactly = patientAgeInDays >= rangeMin && patientAgeInDays <= rangeMax;
                          const matchesWithBuffer = patientAgeInDays >= rangeMin && patientAgeInDays <= (rangeMax + buffer);

                          return {
                            range,
                            minD: rangeMin,
                            maxD: rangeMax,
                            span: rangeMax - rangeMin,
                            matchesExactly,
                            matchesWithBuffer
                          };
                        }).filter(item => item.matchesWithBuffer);

                        if (matchingRangeItems.length === 0) {
                          if (ranges.length === 1 && ageToDays(ranges[0].min_age_range || ranges[0]) === 0 && ageToDays(ranges[0].max_age_range || ranges[0]) === 0) {
                            return {
                              ...comp,
                              minRange: ranges[0]?.value_range?.min_value ?? ranges[0]?.range_min,
                              maxRange: ranges[0]?.value_range?.max_value ?? ranges[0]?.range_max,
                            };
                          }
                          return null;
                        }

                        const exactMatches = matchingRangeItems.filter(item => item.matchesExactly);
                        const finalCandidates = exactMatches.length > 0 ? exactMatches : matchingRangeItems;
                        const bestMatch = [...finalCandidates].sort((a, b) => a.span - b.span)[0];
                        const activeRange = bestMatch.range;

                        return {
                          ...comp,
                          minRange: activeRange?.value_range?.min_value ?? activeRange?.range_min,
                          maxRange: activeRange?.value_range?.max_value ?? activeRange?.range_max,
                        };
                      })
                      .filter(Boolean);

                    const reportId = getReportId(report);
                    const currentPdfImages = pdfImages[reportId] || [];
                    const testName = report?.lab_test?.lab_test_name || report?.data?.lab_test_name || report?.lab_test_name || 'Lab Test';
                    const translatedTestName = t(testName);
                    const isArabic = /[\u0600-\u06FF]/.test(translatedTestName);

                    return (
                      <div
                        key={reportIndex}
                        className={`report-section ${reportIndex > 0 ? 'mt-2 pt-2 ' : ''}`}
                        style={isNewCategory ? { pageBreakBefore: 'always', breakBefore: 'page' } : undefined}
                      >
                        {isComprehensiveReport && (reportIndex === 0 || isNewCategory) && (
                          <div className='mx-3 mb-1 border border-gray-300 bg-gray-100 py-1 text-center text-[12px] font-bold uppercase text-gray-700'>
                            {category}
                          </div>
                        )}
                        <div className='px-3'>
                          <div className='font-bold mb-1 text-gray-900 text-left rtl:text-right text-[14px] border-gray-300 pb-[2px] mt-1 uppercase tracking-tight flex items-center justify-start gap-1'>
                            <span dir={isArabic ? 'rtl' : 'ltr'} className="inline-flex items-center gap-1">
                              <span>{reportIndex + 1}.</span>
                              <bdi>{translatedTestName}</bdi>
                            </span>
                          </div>
                        </div>

                        {filteredComponents.length > 0 ? (
                          <div className='px-3'>
                            <table className='w-full border-collapse mb-2 table-fixed border-spacing-0' style={{ pageBreakInside: 'auto', breakInside: 'auto' }}>
                              <thead>
                                <tr className='border-b-2 border-gray-200 bg-gray-100'>
                                  <th className='font-bold py-1 px-2 text-left rtl:text-right text-[11px] text-gray-700 uppercase w-[25%]'>{t("Name")}</th>
                                  <th className='font-bold py-1 px-2 text-center text-[11px] text-gray-700 uppercase w-[15%]'>{t("Result")}</th>
                                  <th className='font-bold py-1 px-2 text-center text-[11px] text-gray-700 uppercase w-[10%]'>{t("Unit")}</th>
                                  <th className='font-bold py-1 px-2 text-center text-[11px] text-gray-700 uppercase w-[18%]'>{t("Reference")}</th>
                                  <th className='font-bold py-1 px-2 text-center text-[11px] text-gray-700 uppercase w-[12%]'>{t("Status")}</th>
                                  <th className='font-bold py-1 px-2 text-center rtl:text-right text-[11px] text-gray-700 uppercase w-[20%]'>{t("Notes")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredComponents.map((item, index) => {
                                  const status = getResultStatus(item?.value, item?.minRange, item?.maxRange, item);
                                  const statuses = ['normal', 'high', 'low'];
                                  const compComment = item?.comment || "";
                                  const isStatus = statuses.includes(compComment.toLowerCase());
                                  const hasRealComment = compComment && !isStatus;

                                  return (
                                    <tr key={index} className='border-b border-gray-100 last:border-0 align-top'>
                                      <td className='py-[8px] px-2 pl-3 rtl:pl-0 rtl:pr-3 text-[13px] text-gray-900 font-semibold text-left rtl:text-right'>{item?.display_name || item?.name}</td>
                                      <td className='py-[8px] px-2 text-center font-bold text-[12px] capitalize text-gray-900'>{item?.value}</td>
                                      <td className='py-[8px] px-2 text-center text-[11px] capitalize text-gray-600'>{item?.unit}</td>
                                      <td className='py-[8px] px-2 text-center text-[11px] text-gray-600'>{item.control_type === 'DropDown' ? '-' : getRangeValues(item?.minRange, item?.maxRange)}</td>
                                      <td className='py-[8px] px-2 text-center font-bold text-[11px]' style={{ color: item.control_type === 'DropDown' ? 'black' : status.color }}>
                                        {item.control_type === 'DropDown' ? '-' : status.status}
                                      </td>
                                      <td className='py-[8px] px-2 text-left rtl:text-right text-[10px] italic text-gray-500 leading-tight break-words whitespace-pre-wrap'>
                                        {hasRealComment ? compComment : '-'}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          report?.result_type !== "EXTERNAL" && (
                            <div className="px-2 py-2 text-center text-gray-400 italic text-[13px] uppercase tracking-widest">
                              {t("No Results Uploaded")}
                            </div>
                          )
                        )}

                        {report?.result_type === "EXTERNAL" && report?.file && (
                          <div className="px-3 mt-2">
                            <div className="external-report-file text-center flex flex-col items-center justify-center">
                              {pdfLoading ? (
                                <div className="text-center py-4">{t("Loading PDF...")}</div>
                              ) : currentPdfImages.length > 0 ? (
                                currentPdfImages.map((imgSrc, index) => (
                                  <div key={`page_${index + 1}`} className="mb-4">
                                    <img src={imgSrc} alt={`Page ${index + 1}`} className="max-w-full h-auto border border-gray-200" style={{ maxWidth: "700px" }} />
                                  </div>
                                ))
                              ) : (
                                <div className="text-red-500 py-4">{t("Failed to load PDF file.")}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(() => {
                    const allComments = reports
                      .map((r, i) => {
                        const testName = r?.lab_test?.lab_test_name || r?.data?.lab_test_name || r?.lab_test_name || 'Lab Test';
                        const rawComment = r?.comments || r?.data?.comments || '';
                        const comment = typeof rawComment === 'string' ? rawComment.trim() : '';

                        if (!comment ||
                          comment.toLowerCase() === "result entered by lab technician" ||
                          comment.toLowerCase() === "dont add" ||
                          comment.toLowerCase() === "n/a") {
                          return null;
                        }

                        return { i: i + 1, testName, comment };
                      })
                      .filter(Boolean);

                    if (allComments.length === 0) return null;

                    return (
                      <div className='px-3 mt-4 print-avoid-break'>
                        <div className='font-bold text-gray-900 border-b border-black pb-1 mb-2 flex justify-between items-center uppercase'>
                          <span className='text-[13px] tracking-tight'>General Comments</span>
                          <span className='text-[12px]' dir='rtl'>ملاحظات نتائج المختبر</span>
                        </div>
                        <div className='space-y-[2px]'>
                          {allComments.map((item, idx) => (
                            <div key={idx} className='text-[12px] leading-tight flex items-start justify-start gap-1'>
                              <span className='font-bold whitespace-nowrap flex items-center gap-1'>
                                <span>{item.i}.</span>
                                <span>{t(item.testName)}:</span>
                              </span>
                              <span className='text-gray-800'>{item.comment}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    const signatures = reports
                      .map(r => r?.data?.signature || r?.signature)
                      .filter(Boolean);

                    if (signatures.length === 0) return null;

                    const uniqueSignatures = Array.from(new Set(signatures));

                    return (
                      <div className='px-3 flex items-center justify-end gap-8 mt-4 print-avoid-break'>
                        {uniqueSignatures.map((sig, idx) => (
                          <div key={idx} className='flex flex-col items-center'>
                            <span className='text-[10px] font-bold text-gray-600 uppercase mb-1'>{t("Authorized Signature")}</span>
                            <img
                              src={sig}
                              alt='Authorized Signature'
                              className='max-w-[150px] max-h-[45px] object-contain'
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <div className="page-footer-space"></div>
              </td>
            </tr>
          </tfoot>
        </table>

        
        <footer className='print-footer bg-white'>
          {useCbahiHeader ? (
          <div className='border-t border-gray-200 px-3 pb-2 pt-2'>
              {/* Line 1: Received By, Collected By, Uploaded By, Approved By */}
            <div className='flex items-center justify-between mb-1 footer-meta-row'>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Received By")}:</strong>
                <span className='text-gray-600'>{orderDates.receivedBy}</span>
              </div>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Collected By")}:</strong>
                <span className='text-gray-600'>{orderDates.collectedBy}</span>
              </div>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Uploaded By")}:</strong>
                <span className='text-gray-600'>{orderDates.uploadedBy}</span>
              </div>
            </div>  
              <div className='flex items-center justify-between mb-1 footer-meta-row'>
                <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                  <strong className='font-bold mr-2 footer-label'>{t("Approved By")}:</strong>
                  <span className='text-gray-600'>{orderDates.approvedBy}</span>
                </div>
                <div className='text-[10px] text-gray-400 italic'>
                    {t("Printed on")}: {formatDateTime(new Date())}
                  </div>
              </div>
              {/* {(orderDates.signatureUrl || orderDates.stampUrl) && (
                <div className='flex justify-end gap-6 mb-1'>
                  <div className='w-[110px] text-center'>
                    {orderDates.signatureUrl && <img src={orderDates.signatureUrl} alt={t('Approver signature')} className='h-10 w-full object-contain' />}
                    <div className='border-t border-gray-400 text-[9px] text-gray-600'>{t('SIGNATURE')}</div>
                  </div>
                  <div className='w-[110px] text-center'>
                    {orderDates.stampUrl && <img src={orderDates.stampUrl} alt={t('Approver stamp')} className='h-10 w-full object-contain' />}
                    <div className='border-t border-gray-400 text-[9px] text-gray-600'>{t('STAMP')}</div>
                  </div>
                </div>
              )} */}
              <div className='border-t border-gray-200 px-3 pb-2 pt-2' ></div>
              {/* Chai Report Footer */}
              <ChaiReportFooter
                businessInfo={buisnessInfo}
                qrValue={`MRN:${displayPatient?.file_no || displayPatient?.file_number || displayPatient?.patient_mrn || ''}|ORD:${orderNo || ''}`}
              />
          </div>
          ) : (
          <div className='border-t border-gray-200 px-3 pb-2 pt-2'>
            {/* Line 1: Received By, Collected By, Uploaded By */}
            <div className='flex items-center justify-between mb-1 footer-meta-row'>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Received By")}:</strong>
                <span className='text-gray-600'>{orderDates.receivedBy}</span>
              </div>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Collected By")}:</strong>
                <span className='text-gray-600'>{orderDates.collectedBy}</span>
              </div>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Uploaded By")}:</strong>
                <span className='text-gray-600'>{orderDates.uploadedBy}</span>
              </div>
            </div>
            
            {/* Line 2: Approved By (left) and Printed on (right) */}
            <div className='flex items-center justify-between mb-1 footer-meta-row'>
              <div className='text-[11px] text-gray-700 flex items-baseline footer-meta-item'>
                <strong className='font-bold mr-2 footer-label'>{t("Approved By")}:</strong>
                <span className='text-gray-600'>{orderDates.approvedBy}</span>
              </div>
              <div className='text-[10px] text-gray-400 italic'>
                {t("Printed on")}: {formatDateTime(new Date())}
              </div>
            </div>
            
            {/* Line 3: Address (centered) */}
            <div className='flex items-center justify-center border-t border-gray-100 pt-1'>
              <span className='text-[10px] font-semibold mr-1'>{t("Address")}:</span>
              <span className='text-[10px] text-gray-600'>{buisnessInfo?.address || 'N/A'}</span>
            </div>
          </div>
          )}
        </footer>
      </div>

      <style>{`
                .print-table {
                  width: 100%;
                }
                .comprehensive-header-repeat {
                  display: none;
                }
                .lab-report-container {
                    width: 100%;
                }
                .report-page {
                    background: white;
                    padding: 0;
                    margin-bottom: 20px;
                }

                .info-label,
                .footer-label {
                    color: #111827;
                }

                .patient-info-table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }

                .patient-info-table th,
                .patient-info-table td {
                    padding: 1px 6px;
                    color: #111827;
                    line-height: 1.35;
                    text-align: left;
                    vertical-align: top;
                }

                .patient-info-table th {
                    width: 15%;
                    font-weight: 700;
                    text-transform: uppercase;
                    white-space: nowrap;
                }

                .patient-info-table td {
                    width: 35%;
                    font-weight: 600;
                    overflow-wrap: anywhere;
                }
                
                @media print {
                    @page {
                        margin: 0mm !important;
                        size: A4;
                    }
                    
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }

                    .page-header-space {
                      height: ${isComprehensiveReport ? '66mm' : '10mm'};
                    }

                    .page-footer-space {
                     height: ${isChaiFormat ? '32mm' : '22mm'};
                    }
                    
                    .lab-report-container {
                        display: block;
                        width: 100%;
                    }

                    .report-page {
                        display: block;
                        width: 100%;
                        margin: 0 !important;
                        padding: 0mm 7mm 0mm 7mm;
                        box-sizing: border-box;
                        background: white;
                    }

                    .compact-report-page {
                        min-height: 0 !important;
                        height: auto !important;
                    }
                    
                    .print-footer {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        background: white;
                        z-index: 10;
                    }

                    .comprehensive-print-content {
                        display: block;
                        width: 100%;
                        padding-top: ${showReportBarcode ? '84mm' : '72mm'} !important;
                        box-sizing: border-box;
                        -webkit-box-decoration-break: clone;
                        box-decoration-break: clone;
                    }

                    .comprehensive-print-content .print-report-header {
                        position: fixed;
                        top: 0;
                        left: 7mm;
                        right: 7mm;
                        background: white;
                        z-index: 11;
                    }

                    .comprehensive-header-repeat {
                        display: table-header-group !important;
                    }

                    .comprehensive-header-repeat .print-report-header {
                        display: block !important;
                        position: fixed;
                        top: 0;
                        left: 7mm;
                        right: 7mm;
                        background: white;
                        z-index: 11;
                    }

                    .comprehensive-print-content .comprehensive-body-header {
                        display: none !important;
                    }

                    .comprehensive-print-content.supergit-print-content {
                        padding-top: ${showReportBarcode ? '92mm' : '80mm'} !important;
                    }

                    .print-avoid-break,
                    .findings-content,
                    .signature-section,
                    .comments-section {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }

                    .report-section {
                        page-break-inside: auto !important;
                        break-inside: auto !important;
                    }

                    table:not(.print-table) tr {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }

                    .print-table tbody tr {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }

                    .compact-report-page .print-table tbody tr,
                    .compact-report-page .report-section {
                        page-break-inside: auto !important;
                        break-inside: auto !important;
                    }

                    .print-table {
                        width: 100%;
                        border-collapse: collapse;
                        table-layout: fixed;
                    }

                    .print-table thead {
                        display: table-header-group;
                    }

                    .print-table tfoot {
                        display: table-footer-group;
                    }

                    .print-report-content {
                        display: block;
                        width: 100%;
                    }

                    .patient-info-block {
                        color: #111827 !important;
                    }

                    .patient-info-table,
                    .patient-info-table th,
                    .patient-info-table td {
                        color: #111827 !important;
                    }

                    .info-row,
                    .footer-meta-item {
                        min-width: 0;
                    }

                    .info-label {
                        color: #111827 !important;
                        display: inline-block;
                        white-space: nowrap;
                    }

                    .footer-meta-row {
                        gap: 8px;
                    }

                    .footer-meta-item {
                        flex: 1 1 0;
                    }

                    .footer-label {
                        color: #111827 !important;
                        white-space: nowrap;
                    }
                }
            `}</style>
    </div>
  )
}

export default LabReportPrint
